<script>
export default {
  props: {
    bannerMessage: {
      type: String,
      default: '',
    },
    hrefLink: {
      type: String,
      default: '',
    },
    hrefLinkText: {
      type: String,
      default: '',
    },
    hasActionButton: {
      type: Boolean,
      default: false,
    },
    actionButtonVariant: {
      type: String,
      default: '',
    },
    actionButtonLabel: {
      type: String,
      default: '',
    },
    actionButtonIcon: {
      type: String,
      default: 'arrow-right',
    },
    colorScheme: {
      type: String,
      default: '',
    },
    hasCloseButton: {
      type: Boolean,
      default: false,
    },
    closeButtonLabel: {
      type: String,
      default: '',
    },
    align: {
      type: String,
      default: 'center',
    },
    leadingIcon: {
      type: String,
      default: '',
    },
  },
  computed: {
    bannerClasses() {
      const classList = [this.colorScheme];

      classList.push(
        this.align === 'left' ? 'justify-between' : 'justify-center'
      );

      if (this.hasActionButton || this.hasCloseButton) {
        classList.push('has-button');
      }
      return classList;
    },
  },
  methods: {
    onClick(e) {
      this.$emit('click', e);
    },
    onClickClose(e) {
      this.$emit('close', e);
    },
  },
};
</script>

<template>
  <div
    class="flex items-center h-12 gap-4 px-4 py-3 text-xs text-white banner dark:text-white"
    :class="bannerClasses"
  >
    <span class="banner-message">
      <fluent-icon
        v-if="leadingIcon"
        :icon="leadingIcon"
        size="12"
        class="banner-leading-icon shrink-0"
      />
      <span class="banner-text">
        {{ bannerMessage }}
        <a
          v-if="hrefLink"
          :href="hrefLink"
          rel="noopener noreferrer nofollow"
          target="_blank"
        >
          {{ hrefLinkText }}
        </a>
      </span>
    </span>
    <div class="actions">
      <woot-button
        v-if="hasActionButton"
        size="tiny"
        :icon="actionButtonIcon"
        :variant="actionButtonVariant"
        color-scheme="primary"
        class-names="banner-action__button"
        @click="onClick"
      >
        {{ actionButtonLabel }}
      </woot-button>
      <woot-button
        v-if="hasCloseButton"
        size="tiny"
        :color-scheme="colorScheme"
        icon="dismiss-circle"
        class-names="banner-action__button"
        @click="onClickClose"
      >
        {{ closeButtonLabel || $t('GENERAL_SETTINGS.DISMISS') }}
      </woot-button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.banner {
  &.primary {
    @apply bg-woot-500 dark:bg-woot-500;
    .banner-action__button {
      @apply bg-woot-600 dark:bg-woot-600 border-none text-white;

      &:hover {
        @apply bg-woot-700 dark:bg-woot-700;
      }
    }
  }

  &.secondary {
    @apply bg-slate-200 dark:bg-slate-300 text-slate-800 dark:text-slate-800;
    a {
      @apply text-slate-800 dark:text-slate-800;
    }
  }

  &.alert {
    @apply bg-red-500 dark:bg-red-500;
    .banner-action__button {
      @apply bg-red-700 dark:bg-red-700 border-none text-white dark:text-white;

      &:hover {
        @apply bg-red-800 dark:bg-red-800;
      }
    }
  }

  &.warning {
    @apply bg-yellow-500 dark:bg-yellow-500 text-yellow-900 dark:text-yellow-900;
    .banner-action__button {
      @apply bg-yellow-700 dark:bg-yellow-700 border-none text-white dark:text-white;

      &:hover {
        @apply bg-yellow-800 dark:bg-yellow-800;
      }
    }
    a {
      @apply text-yellow-900 dark:text-yellow-900;
    }
  }

  &.success {
    @apply bg-green-700 dark:bg-green-700;
    .banner-action__button {
      @apply bg-green-800 dark:bg-green-800 border-none text-white dark:text-white;

      &:hover {
        @apply bg-green-900 dark:bg-green-900;
      }
    }
  }

  &.gray {
    @apply text-black-500 dark:text-black-500;
    .banner-action__button {
      @apply text-white dark:text-white;
    }
  }

  &.alert-soft,
  &.primary-soft {
    @apply w-full h-auto px-3 py-2 rounded-xl border border-solid;

    a {
      @apply underline;
    }
  }

  &.alert-soft {
    @apply bg-red-light-4 border-red-light-3 dark:border-red-light text-gray-gray-11 dark:text-white;

    .dark & {
      background-color: rgba(225, 14, 14, 0.64);
    }

    a {
      @apply text-gray-gray-11 dark:text-white;
    }

    .banner-leading-icon {
      @apply text-red-dark dark:text-white;
    }
  }

  &.primary-soft {
    @apply bg-orange-light-5 border-orange-light-4 dark:border-orange-brand text-gray-gray-11 dark:text-white;

    .dark & {
      background-color: rgba(255, 134, 56, 0.24);
    }

    a {
      @apply text-gray-gray-11 dark:text-white;
    }

    .banner-leading-icon {
      @apply text-orange-brand dark:text-white;
    }
    .banner-action__button {
      @apply bg-white dark:bg-gray-gray-12 border border-solid border-gray-gray-8 dark:border-gray-gray-12 text-gray-gray-17 dark:text-white;
      border-radius: 8px !important;
      flex-direction: row-reverse;

      ::v-deep .icon {
        @apply text-orange-brand dark:text-white;
      }
      &:hover {
        @apply bg-gray-gray-1 dark:bg-gray-gray-12;
      }
    }
  }

  &.primary-soft.has-button {
    @apply pl-3 pr-1 py-1;
  }

  a {
    @apply ml-1 underline text-white dark:text-white text-xs;
  }

  .banner-action__button {
    ::v-deep .button__content {
      @apply whitespace-nowrap;
    }
  }

  .banner-message {
    @apply flex items-center gap-2;
  }

  .banner-text {
    @apply flex-1 min-w-0;

    a {
      @apply ml-0;
    }
  }

  .actions {
    @apply flex gap-1 right-3;
  }
}
</style>
